import {useLocation} from 'react-router-dom';
import {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import logoImage from '../assets/kool-logo.png';
import LangButton from './LangButton'
import LangTextButton from './LangTextButton'

/**
 * DropdownMenu Component
 * @param {Object} props - props for component
 * @param {string} props.name - name for component
 * @param {list} props.items - list of sub components button
 * @param {isSelected} props.isSelected - set true to highlight the menu (default: false)
 * @param {string} [props.color] - font bg color for button (default: '#5ADBFF')
 * @param {string} [props.colorFont] - hover font color (default: '#303030')
 * @param {Function} [props.onClick] - click handler
 * @returns {JSX.Element} - DropdownMenu component
 */

const DropdownMenu = ({ name, items, isSelected=false, color="#5ADBFF", colorFont="#303030", onClick, cursor}) => {
    onClick = onClick ? onClick : ()=>{};

    return (
      <>
        <div className="font-pretendard group h-full relative">
          <button
            className={
              "h-full font-pretendard px-5 text-[0.9rem] group-hover:bg-gray-100 group-hover:text-[" +
              colorFont +
              "]" +
              (cursor === "default" ? " cursor-default " : " ") +
              " group-hover:bg-[" +
              color +
              "] transform transition-all duration-300"
            }
            onClick={onClick}
          >
            {isSelected ? (
              <span className="underline underline-offset-8 decoration-brand-kool-blue decoration-[3px]">
                {name && name}
              </span>
            ) : (
              <span>{name && name}</span>
            )}
          </button>

          {items && (
            <div className="absolute top-full text-[0.9rem] group-hover:shadow-lg pointer-events-none opacity-0 w-full group-hover:pointer-events-auto group-hover:opacity-100 transform transition-all duration-300">
              <ul>
                {items.map((item, index) => (
                  <li key={index} className="text-center">
                    <DropdownItem
                      name={item.name}
                      location={item.location}
                      items={item.children}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </>
    );
};
const DropdownItem = ({name, location, items}) => {
    const navigate = useNavigate();
    return (
      <>
        <div className="h-full relative p-0 group/item">
          <button
            className={
              "w-full bg-brand-kool-blue text-gray-50 hover:bg-brand-darker-blue py-2 transition duration-300" +
              (location === "" ? " cursor-default " : "")
            }
            onClick={
              location &&
              (() => {
                navigate(location);
              })
            }
          >
            {name}
          </button>
          {items && (
            <div className="absolute left-full top-0 pointer-events-none opacity-0 w-full group-hover/item:pointer-events-auto group-hover/item:opacity-100 transform transition-all duration-300">
              <ul className="p-0 m-0">
                {items.map((item, index) => (
                  <li key={index} className="text-center">
                    <button
                      className={
                        "w-full bg-brand-silicon-white hover:bg-brand-electric-blue text-black py-2 transition duration-300"
                      }
                      onClick={() => {
                        navigate(item.location);
                      }}
                    >
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </>
    );
}

const ToLandingPageButton = () => {
    return (
      <>
        <Link
          to="/CES25"
          className="text-[0.8rem] justify-center font-thin text-center flex rounded-full transform transition-all duration-300
        border-b-2 border-black hover:border-brand-darker-blue text-white hover:text-black hover:bg-white bg-black sm:px-4 px-2 py-[0.1rem]"
        >
          <span className="font-pretendard">CES 2025</span>
        </Link>
      </>
    );
}

const MenuButton = ({ name, items, isSelected=false, onClick, toggleMenu, children }) => {
  const navigate = useNavigate();
  let location = useLocation();
  return (
    <>
      <div className="w-full ps-5">
        {isSelected ? (
          <button
            className="w-full py-4 ps-5 text-left font-pretendard"
            onClick={onClick}
          >
            {name}
          </button>
        ) : (
          <button
            className="w-full py-4 ps-5 text-left font-pretendard"
            onClick={onClick}
          >
            {name}
          </button>
        )}
        {children &&
          children.map((info, index) => (
            <MenuButton
              name={info.name}
              key={info.location}
              onClick={() => {
                navigate(info.location);
                toggleMenu();
              }}
              items={info.children}
              isSelected={info.location === location.pathname}
            />
          ))}
      </div>
    </>
  );
}

const MenuBar = ({color}) => {
    const navigate = useNavigate();
    let location = useLocation();
    let [isSliding, setIsSliding] = useState(false);
    let [isSlideIn, setIsSlideIn] = useState(false);

    const toggleMenu = () => {
        if(!isSliding) {
            setIsSliding(true);
            if(isSlideIn) {
                setIsSlideIn(false);
            } else {
                setIsSlideIn(true);
            }
            setTimeout(()=>{
                setIsSliding(false);
            },200);
        }
    }
    
    class ItemInfo {
      /**
       * Represents an item with a name, location, and an optional child item.
       * @param {string} name - The name of the item
       * @param {string} location - The location of the item
       * @param {ItemInfo} [children] - Optional child item
       */
      constructor(name, location, children = undefined) {
        this.name = name;
        this.location = location;
        this.children = children;
      }
    }

    const pageInfos = [
      new ItemInfo("Home", "/"),
      new ItemInfo("Technology", "/technology"),
      new ItemInfo("Product", "/product"),
      new ItemInfo("Career", "/career"),
      new ItemInfo("Company", "/company", [
        new ItemInfo("About Us", "/company/about-us"),
        new ItemInfo("Newsroom", "/company/news"),
      ]),
      new ItemInfo("Contact", "/contact"),
    ];
    
    return (
      <>
        <nav className="sm:flex hidden h-[4rem] flex-row shadow bg-white border-b-2 border-brand-darker-blue">
          <Link to="/" className="flex py-4 ps-5 pe-3">
            <img
              src={logoImage}
              alt=""
              className="xl:block hidden w-auto object-contain"
            />
            <img
              src={require("../assets/icon/koolmicro.png")}
              alt=""
              className="xl:hidden block w-auto object-contain"
            />
          </Link>
          <div className="lg:flex hidden items-center">
            {/* <ToLandingPageButton /> */}
          </div>

          <div className="flex ms-auto">
            <div className="sm:flex hidden items-center">
              {pageInfos.map((info, index) => (
                <DropdownMenu
                  name={info.name}
                  key={info.location}
                  onClick={
                    info.location === ""
                      ? null
                      : () => {
                          navigate(info.location);
                        }
                  }
                  cursor={info.location === "" ? "default" : null}
                  items={info.children}
                  isSelected={
                    info.location.split("/")[1] ===
                    location.pathname.split("/")[1]
                  }
                />
              ))}
            </div>
          </div>
          <div className="flex ms-6 me-5 justify-center items-center">
            <a
              href="https://www.youtube.com/@KoolMicro"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-8 group"
            >
              <img
                src={require("../assets/icon/youtube.png")}
                className="absolute h-4 opacity-100 group-hover:opacity-0 transition-opacity duration-300"
                alt=""
              />
              <img
                src={require("../assets/icon/youtube-col.png")}
                className="absolute h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                alt=""
              />
            </a>
            <a
              href="https://www.linkedin.com/company/koolmicro/posts/?feedView=all"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-8 group"
            >
              <img
                src={require("../assets/icon/linkedin.png")}
                className="absolute h-4 opacity-100 group-hover:opacity-0 transition-opacity duration-300"
                alt=""
              />
              <img
                src={require("../assets/icon/linkedin-col.png")}
                className="absolute h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                alt=""
              />
            </a>
            <LangButton />
          </div>
        </nav>

        {/* 모바일 메뉴 */}
        <nav className="sm:hidden flex flex-row h-[4rem] bg-white border-b-brand-darker-blue border-b-[1px]">
          <button
            className="flex flex-col justify-center px-3 rounded-xl"
            onClick={() => {
              toggleMenu();
            }}
          >
            <div className="rounded-full w-6 h-1 bg-gray-300 mb-1"></div>
            <div className="rounded-full w-6 h-1 bg-gray-300 mb-1"></div>
            <div className="rounded-full w-6 h-1 bg-gray-300"></div>
          </button>
          <div className="ms-1 my-auto w-[5rem] text-center mobile-hide">
            {/* <ToLandingPageButton /> */}
          </div>

          <div className="ms-auto flex justify-end items-center me-3">
            <div className="flex h-full items-center justify-end">
              <Link to="/" className="flex h-[2rem] py-1 overflow-hidden">
                <img
                  alt="koolmicro"
                  src={require("../assets/kool-logo.png")}
                  className="ms-auto w-auto h-full object-contain"
                />
              </Link>
            </div>
          </div>
          <div
            className={
              "font-pretendard text-black fixed flex w-full top-0 mt-[3.9rem] h-[100svh]" +
              (isSlideIn ? " flex " : isSliding ? " flex " : " hidden ") +
              (isSliding
                ? isSlideIn
                  ? " animate-slide-right-in "
                  : " animate-slide-right-out "
                : "")
            }
          >
            <ul className="flex relative bg-gray-100 w-[20rem] flex-col shadow-md ">
              {pageInfos.map((info, index) => (
                <MenuButton
                  name={info.name}
                  key={info.location}
                  toggleMenu={toggleMenu}
                  onClick={() => {
                    navigate(info.location);
                    toggleMenu();
                  }}
                  children={info.children}
                  items={info.children}
                  isSelected={info.location === location.pathname}
                />
              ))}
              <LangTextButton />
            </ul>
            <div
              className="flex-grow"
              onClick={() => {
                toggleMenu();
              }}
            ></div>
          </div>
        </nav>
      </>
    );
};

export default MenuBar;